'use client';

import Image from 'next/image';
import { IMAGE_NAMES } from '@/config/imageConstants';
import { getImageUrls } from '@/utils/imageUtils';

interface WebPImageProps {
  webpName: string;
  fallbackName: string;
  alt: string;
  fill?: boolean;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  fetchPriority?: 'auto' | 'high' | 'low';
  unoptimized?: boolean;
  loading?: 'lazy' | 'eager';
}

/**
 * Next.js Image component with WebP fallback support using <picture> element
 * This ensures only ONE image is loaded - browser chooses the best format
 */
export function WebPImage({
  webpName,
  fallbackName,
  alt,
  fill,
  width,
  height,
  className,
  priority,
  fetchPriority,
  unoptimized,
  loading,
}: WebPImageProps) {
  // Use getImageUrls to get proxy URLs with proper cache headers
  const { webpUrl, fallbackUrl } = getImageUrls(webpName, fallbackName);

  // Use picture element to let browser choose the format - only ONE image loads
  // Use regular <img> instead of Next.js Image to avoid double loading
  if (fill) {
    return (
      <picture className={className} style={{ position: 'absolute', inset: 0 }}>
        <source srcSet={webpUrl} type="image/webp" />
        <img
          src={fallbackUrl}
          alt={alt}
          className="object-cover w-full h-full"
          style={{ objectFit: 'cover' }}
          loading={loading === 'lazy' ? 'lazy' : 'eager'}
          fetchPriority={fetchPriority}
        />
      </picture>
    );
  }

  return (
    <picture className={className}>
      <source srcSet={webpUrl} type="image/webp" />
      <img
        src={fallbackUrl}
        alt={alt}
        width={width}
        height={height}
        className="object-contain"
        loading={loading}
        fetchPriority={fetchPriority}
      />
    </picture>
  );
}

/**
 * Regular img tag with WebP fallback support using <picture> element
 * This ensures only ONE image is loaded - browser chooses the best format
 */
interface WebPImgProps {
  webpName: string;
  fallbackName: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: 'lazy' | 'eager';
}

export function WebPImg({
  webpName,
  fallbackName,
  alt,
  width,
  height,
  className,
  loading,
}: WebPImgProps) {
  // Use getImageUrls to get proxy URLs with proper cache headers
  const { webpUrl, fallbackUrl } = getImageUrls(webpName, fallbackName);

  return (
    <picture className={className}>
      <source srcSet={webpUrl} type="image/webp" />
      <img
        src={fallbackUrl}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
      />
    </picture>
  );
}

